import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { Download, Settings } from "lucide-react";
import { useLang } from "@/App";

export const Logo = ({ size = 40 }) => {
    const { logoChar } = useLang();
    const lucy = logoChar === "lucy";
    return (
        <span
            data-testid="kiber-logo"
            className="inline-block overflow-hidden shrink-0"
            style={{
                width: size,
                height: size,
                borderRadius: size * 0.26,
                background: "#1c0f33",
                border: `1px solid ${lucy ? "rgba(255,75,216,0.65)" : "rgba(124,58,237,0.65)"}`,
                boxShadow: lucy ? "0 0 14px rgba(255,75,216,0.4)" : "0 0 14px rgba(124,58,237,0.4)",
            }}
        >
            <img
                src={lucy ? "/char/av-lucy.png" : "/char/av-david.png"}
                alt="Kiber AI"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
        </span>
    );
};

const LINKS = [
    { to: "/", key: "home", testid: "nav-home-link" },
    { to: "/chat", key: "chat", testid: "nav-chat-link" },
    { to: "/characters", key: "characters", testid: "nav-characters-link" },
    { to: "/android", key: "android", testid: "nav-android-link" },
];

export default function Navbar() {
    const { lang, setLang, t } = useLang();
    return (
        <motion.header
            initial={{ y: -70, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 left-0 right-0 z-50 border-b border-[#212b45]/80 bg-[#080a12]/75 backdrop-blur-xl"
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
                <NavLink to="/" className="flex items-center gap-3" data-testid="nav-home-btn">
                    <Logo size={34} />
                    <span className="hidden sm:block text-left">
                        <span className="font-display font-800 tracking-[0.22em] text-sm block leading-none font-bold">
                            KIBER <span className="neon-text">AI</span>
                        </span>
                        <span className="font-mono2 text-[9px] text-slate-500 tracking-widest">CYBERPUNK COMPANION</span>
                    </span>
                </NavLink>

                <nav className="hidden md:flex items-center gap-7 ml-10 font-mono2 text-[11px] tracking-[0.18em]">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `uppercase transition-colors ${isActive ? "text-[#d7ff35]" : "text-slate-400 hover:text-[#d7ff35]"}`
                            }
                        >
                            {t.nav[l.key]}
                        </NavLink>
                    ))}
                </nav>

                <div className="ml-auto flex items-center gap-3">
                    <span className="hidden lg:flex items-center gap-2 font-mono2 text-[10px] tracking-widest text-[#49f5e8]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#49f5e8] pulse-dot" />
                        {t.status}
                    </span>

                    <NavLink
                        to="/settings"
                        data-testid="nav-settings-link"
                        aria-label={t.nav.settings}
                        className={({ isActive }) =>
                            `w-9 h-9 grid place-items-center rounded-full border transition-colors ${
                                isActive
                                    ? "border-[#fcee0a] text-[#fcee0a]"
                                    : "border-[#212b45] text-slate-400 hover:text-[#fcee0a] hover:border-[#fcee0a]/60"
                            }`
                        }
                    >
                        <Settings size={15} />
                    </NavLink>

                    <div className="flex border border-[#212b45] rounded-full overflow-hidden font-mono2 text-[10px]" data-testid="lang-switch">
                        <button
                            data-testid="lang-switch-ua"
                            onClick={() => setLang("ua")}
                            className={`px-3 py-1.5 transition-colors ${lang === "ua" ? "bg-[#d7ff35] text-[#080a12] font-bold" : "text-slate-400 hover:text-white"}`}
                        >
                            UA
                        </button>
                        <button
                            data-testid="lang-switch-en"
                            onClick={() => setLang("en")}
                            className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-[#d7ff35] text-[#080a12] font-bold" : "text-slate-400 hover:text-white"}`}
                        >
                            EN
                        </button>
                    </div>
                    <a
                        data-testid="nav-download-btn"
                        href="/downloads/KiberAI.apk"
                        download
                        className="hidden lg:flex items-center gap-2 bg-[#d7ff35] text-[#080a12] font-bold text-xs px-4 py-2 rounded-full hover:shadow-[0_0_24px_rgba(215,255,53,0.5)] transition-shadow"
                    >
                        <Download size={14} />
                        {t.nav.download}
                    </a>
                </div>
            </div>
        </motion.header>
    );
}
